import { COOKIE_NAME } from "@/helpers/sessionCookies";
import axios from "axios";
import { NextRequest } from 'next/server';

const BACKEND_URL =
   process.env.NEXT_PUBLIC_API_URL_BASE || 'http://localhost:5050';

// Variable para almacenar el token actual
let currentToken: string | null = null;

// Función para obtener un token temporal
const getTempTkn = async () => {
  try {
    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_API_URL_BASE}/auth/temp-tkn`
    );
    const tempTkn = response.data.token;
    
    // Actualizar el token actual
    currentToken = tempTkn;
    return tempTkn;
  } catch (error) {
    //console.error("Error obteniendo token temporal:", error);
    throw error; // Propaga el error para que se maneje externamente
  }
};

// Función para crear instancia de axios para API Routes (servidor)
export const createAxiosInstance = async (request: NextRequest) => {
  // Obtener el token de la cookie mpsession del request
  const mpsession = request.cookies.get(COOKIE_NAME)?.value;

  const headers: any = {
    'Content-Type': 'application/json',
  };

  // Agregar Authorization header si existe el token
  if (mpsession) {
    headers.Authorization = `Bearer ${mpsession}`;
    //console.log('✅ [DEBUG] Authorization header agregado:', `Bearer ${mpsession.substring(0, 20)}...`);
  } else {
    // Si no hay mpsession, intentar obtener un token temporal
    try {
      const tempToken = await getTempTkn();
      headers.Authorization = `Bearer ${tempToken}`;
    } catch (error) {
      //console.error('❌ [AUTH] Error al obtener token temporal:', error);
    }
  }

  return axios.create({
    baseURL: BACKEND_URL,
    withCredentials: true,
    headers,
  });
};

// Instancia por defecto para casos donde no necesites el request
const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests
axiosInstance.interceptors.request.use(
  async (config) => {
    //console.log(`🚀 [REQUEST] ${config.method?.toUpperCase()} ${config.url}`);
    
    // Si no hay token actual, intentar obtener uno temporal
    if (!currentToken) {
      try {
        await getTempTkn();
      } catch (error) {
        //console.error('❌ [AUTH] Error al obtener token temporal en interceptor:', error);
      }
    }
    
    // Si hay un token disponible, agregarlo al header
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    
    //console.log('🚀 [REQUEST] Headers:', config.headers);
    return config;
  },
  (error) => {
    //console.error('❌ [REQUEST ERROR]:', error);
    return Promise.reject(error);
  }
);

// Interceptor para responses
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    //console.error('❌ [RESPONSE ERROR]:', {
    //  url: error.config?.url,
    //  status: error.response?.status,
    //  data: error.response?.data,
    //  message: error.message,
    //});

    if (error.response?.status === 401) {
      //console.warn('🔐 [AUTH] Unauthorized access - posible problema con el token');
      // Intentar renovar el token si hay un error 401
      currentToken = null;
    }

    if (error.response?.status >= 500) {
      //console.error('🔥 [SERVER] Error del servidor');
    }

    return Promise.reject(error);
  }
);

// Funciones helper para diferentes tipos de requests
export const apiGet = async (url: string, config = {}) => {
  const response = await axiosInstance.get(url, config);
  
  return response.data;
};

export const apiPost = async (url: string, data = {}, config = {}) => {
  const response = await axiosInstance.post(url, data, config);
  return response.data;
};

export const apiPut = async (url: string, data = {}, config = {}) => {
  const response = await axiosInstance.put(url, data, config);
  return response.data;
};

export const apiDelete = async (url: string, config = {}) => {
  const response = await axiosInstance.delete(url, config);
  return response.data;
};

export const apiPatch = async (url: string, data = {}, config = {}) => {
  const response = await axiosInstance.patch(url, data, config);
  return response.data;
};

// Función para actualizar el token manualmente
export const updateAxiosToken = (newToken: string) => {
  currentToken = newToken;
};

export default axiosInstance;
